import { createFileRoute, Link, Outlet, useParams } from "@tanstack/react-router"
import {
  Bell,
  CalendarCog,
  Mail,
  Flag,
  ClipboardList,
  Globe,
  Tags,
  Palette,
  Ticket,
  // ShieldCheck, // re-add when the Permissions nav item below is restored
  Plug,
} from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/core/utils"

// Parent layout for the Event Settings suite. A left sub-nav + nested section
// routes render into the canvas. The root is `relative h-full` so any
// ResponsiveSheet mounted by a section (field/group create-edit) covers ONLY
// this canvas — the shell rail/top bar stay interactive.
export const Route = createFileRoute("/$orgSlug/$eventSlug/settings")({
  component: SettingsLayout,
})

type NavItem = {
  to: string
  label: string
  icon: ReactNode
}

type NavGroup = { title: string; items: NavItem[] }

// Grouped by journey: set up the event, get people in, make it look right,
// talk to guests, then the data model + external tools.
const NAV_GROUPS: NavGroup[] = [
  {
    title: "Setup",
    items: [
      { to: "/$orgSlug/$eventSlug/settings/details", label: "Event details", icon: <CalendarCog /> },
      { to: "/$orgSlug/$eventSlug/settings/feature-flags", label: "App features", icon: <Flag /> },
    ],
  },
  {
    title: "Registration",
    items: [
      { to: "/$orgSlug/$eventSlug/settings/registration", label: "Registration", icon: <ClipboardList /> },
      { to: "/$orgSlug/$eventSlug/settings/ticketing", label: "Tickets", icon: <Ticket /> },
      { to: "/$orgSlug/$eventSlug/settings/site", label: "Event site", icon: <Globe /> },
    ],
  },
  {
    title: "Appearance",
    items: [
      { to: "/$orgSlug/$eventSlug/settings/branding", label: "Branding", icon: <Palette /> },
    ],
  },
  {
    title: "Communications",
    items: [
      { to: "/$orgSlug/$eventSlug/settings/invites", label: "Invites & emails", icon: <Mail /> },
      { to: "/$orgSlug/$eventSlug/settings/reminders", label: "Reminders", icon: <Bell /> },
    ],
  },
  {
    title: "Data & integrations",
    items: [
      { to: "/$orgSlug/$eventSlug/settings/attendee-data", label: "Attendee data", icon: <Tags /> },
      // Permissions page hidden for now — route + page kept intact, just un-listed.
      // { to: "/$orgSlug/$eventSlug/settings/permissions", label: "Permissions", icon: <ShieldCheck /> },
      { to: "/$orgSlug/$eventSlug/settings/integrations", label: "Integrations", icon: <Plug /> },
    ],
  },
]

function SettingsLayout() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/settings" })

  return (
    <div className="relative flex h-full min-h-0">
      {/* sub-nav — collapses to an icon rail on narrow canvases */}
      <nav className="flex w-[58px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-plum-200/50 p-2 @[560px]:w-[200px] @[560px]:p-3 dark:border-white/10">
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.title} className={cn(gi > 0 && "mt-3")}>
            {/* On the icon rail the header collapses to a hairline divider. */}
            <p className="mb-1 hidden px-2 text-[11px] font-semibold uppercase tracking-wide text-plum-700/40 @[560px]:block dark:text-plum-200/30">
              {group.title}
            </p>
            {gi > 0 && (
              <div className="mx-2 mb-2 border-t border-plum-200/50 @[560px]:hidden dark:border-white/10" />
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  params={params}
                  className={cn(
                    "group flex items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium text-plum-800/80 transition hover:bg-plum-100/60 @[560px]:px-3 dark:text-plum-200/70 dark:hover:bg-white/5",
                    "[&.active]:bg-[var(--accent)]/12 [&.active]:text-[var(--accent)] [&.active]:font-semibold",
                  )}
                  activeProps={{ className: "active" }}
                  title={item.label}
                >
                  <span className="grid h-5 w-5 shrink-0 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
                    {item.icon}
                  </span>
                  <span className="hidden truncate @[560px]:block">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="relative min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
